/**
 * Product Gallery Navigation with Vertical Bar
 * Handles image navigation, keyboard controls, swipe gestures, and accessibility
 */

class ProductGalleryNavigation {
  constructor(galleryElement) {
    this.gallery = galleryElement;
    this.navSegments = this.gallery.querySelectorAll('.nav-bar-segment');
    this.slides = this.gallery.querySelectorAll('.gallery-slide');
    this.slider = this.gallery.querySelector('.product-gallery-slider');
    this.navBar = this.gallery.querySelector('.product-gallery-navigation');

    // Validate required elements
    if (
      !this.slider ||
      this.slides.length === 0 ||
      this.navSegments.length === 0
    ) {
      console.warn('Product gallery: Missing required elements');
      return;
    }

    // Configuration
    this.swipeThreshold = 50;
    this.touchStartX = 0;
    this.touchEndX = 0;

    // Initialize
    this.init();
  }

  init() {
    this.setupNavigation();
    this.setupKeyboardControls();
    this.setupSwipeControls();
    this.setupAccessibility();
  }

  /**
   * Change active slide
   * @param {number} index - Slide index to activate
   */
  changeSlide(index) {
    if (index < 0 || index >= this.slides.length) return;

    const previousIndex = parseInt(this.slider.dataset.currentSlide) || 0;

    // Pause any playing media before switching
    this.pauseAllMedia();

    // Update slides
    this.slides.forEach((slide, i) => {
      slide.classList.toggle('active', i === index);
      slide.setAttribute('aria-hidden', i !== index);
    });

    // After slides.forEach — load deferred media on the active slide
    // const activeSlide = this.slides[index];
    // const deferredMedia = activeSlide?.querySelector('deferred-media');
    // if (deferredMedia && !deferredMedia.hasAttribute('loaded')) {
    //   // loadContent() initialises the player but doesn't autoplay
    //   deferredMedia.loadContent?.(false);
    // }

    // Update navigation with proper tabindex management
    this.navSegments.forEach((segment, i) => {
      const isActive = i === index;
      segment.classList.toggle('active', isActive);
      segment.setAttribute('aria-selected', isActive);
      segment.setAttribute(
        'aria-label',
        `View image ${i + 1} of ${this.slides.length}`,
      );
      // Only active tab should be focusable
      segment.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    // After navSegments.forEach(...) — scroll active thumb into view
    if (this.navBar) {
      const activeSegment = this.navSegments[index];
      if (activeSegment) {
        // Center the active thumb in the strip instead of just making it visible
        const targetScrollLeft =
          activeSegment.offsetLeft -
          this.navBar.offsetWidth / 2 +
          activeSegment.offsetWidth / 2;

        this.navBar.scrollTo({
          left: targetScrollLeft,
          behavior: 'smooth',
        });
      }
    }

    // Update current slide data
    this.slider.dataset.currentSlide = index;

    // Announce to screen readers
    this.announceSlideChange(index);

    // Focus management - only focus if user navigated via keyboard
    if (
      document.activeElement &&
      document.activeElement.classList.contains('nav-bar-segment')
    ) {
      this.navSegments[index]?.focus();
    }
  }

  /**
   * Change slide by media ID (for variant image switching)
   * @param {string} mediaId - The media ID in format "section-id-media-id"
   * @returns {boolean} - True if slide was found and changed
   */
  changeSlideByMediaId(mediaId) {
    // Find the slide with matching data-media-id
    const targetSlide = Array.from(this.slides).find(
      (slide) => slide.dataset.mediaId === mediaId,
    );

    if (targetSlide) {
      const index = parseInt(targetSlide.dataset.slideIndex);
      if (!isNaN(index)) {
        this.changeSlide(index);
        return true;
      }
    }

    // If exact match not found, try matching just the media ID part
    const mediaIdPart = mediaId.split('-').pop();
    const partialMatchSlide = Array.from(this.slides).find((slide) => {
      const slideMediaId = slide.dataset.mediaId;
      return slideMediaId && slideMediaId.endsWith('-' + mediaIdPart);
    });

    if (partialMatchSlide) {
      const index = parseInt(partialMatchSlide.dataset.slideIndex);
      if (!isNaN(index)) {
        this.changeSlide(index);
        return true;
      }
    }

    console.log(
      '[ProductGalleryNavigation] No slide found for mediaId:',
      mediaId,
    );
    return false;
  }

  /**
   * Setup navigation click handlers using event delegation
   */
  setupNavigation() {
    if (!this.navBar) return;

    // Use event delegation for better performance
    this.navBar.addEventListener('click', (e) => {
      const segment = e.target.closest('.nav-bar-segment');
      if (!segment) return;

      const index = parseInt(segment.dataset.mediaIndex);
      if (!isNaN(index)) {
        this.changeSlide(index);
      }
    });
  }

  /**
   * Setup keyboard navigation
   */
  setupKeyboardControls() {
    this.gallery.addEventListener('keydown', (e) => {
      const currentIndex = parseInt(this.slider.dataset.currentSlide) || 0;

      switch (e.key) {
        case 'ArrowDown':
        case 'ArrowRight':
          e.preventDefault();
          this.navigateNext(currentIndex);
          break;

        case 'ArrowUp':
        case 'ArrowLeft':
          e.preventDefault();
          this.navigatePrevious(currentIndex);
          break;

        case 'Home':
          e.preventDefault();
          this.changeSlide(0);
          break;

        case 'End':
          e.preventDefault();
          this.changeSlide(this.slides.length - 1);
          break;
      }
    });
  }

  /**
   * Navigate to next slide
   */
  navigateNext(currentIndex) {
    const nextIndex =
      currentIndex < this.slides.length - 1 ? currentIndex + 1 : 0;
    this.changeSlide(nextIndex);
  }

  /**
   * Navigate to previous slide
   */
  navigatePrevious(currentIndex) {
    const prevIndex =
      currentIndex > 0 ? currentIndex - 1 : this.slides.length - 1;
    this.changeSlide(prevIndex);
  }

  /**
   * Setup swipe controls for mobile
   */
  setupSwipeControls() {
    this.slider.addEventListener(
      'touchstart',
      (e) => {
        this.touchStartX = e.changedTouches[0].screenX;
      },
      { passive: true },
    );

    this.slider.addEventListener(
      'touchend',
      (e) => {
        this.touchEndX = e.changedTouches[0].screenX;
        this.handleSwipe();
      },
      { passive: true },
    );
  }

  /**
   * Handle swipe gesture
   */
  handleSwipe() {
    const currentIndex = parseInt(this.slider.dataset.currentSlide) || 0;
    const swipeDistance = this.touchStartX - this.touchEndX;

    if (Math.abs(swipeDistance) < this.swipeThreshold) return;

    if (swipeDistance > 0) {
      // Swipe up - next image
      this.navigateNext(currentIndex);
    } else {
      // Swipe down - previous image
      this.navigatePrevious(currentIndex);
    }
  }

  /**
   * Setup accessibility attributes
   */
  setupAccessibility() {
    // Add role and aria attributes to gallery
    this.gallery.setAttribute('role', 'region');
    this.gallery.setAttribute('aria-label', 'Product image gallery');

    // Add role to nav bar
    if (this.navBar) {
      this.navBar.setAttribute('role', 'tablist');
      this.navBar.setAttribute('aria-label', 'Image navigation');
    }

    // Add roles and attributes to segments
    this.navSegments.forEach((segment, i) => {
      segment.setAttribute('role', 'tab');
      segment.setAttribute('aria-controls', `slide-${i}`);
      segment.setAttribute('tabindex', i === 0 ? '0' : '-1');
    });

    // Add roles to slides
    this.slides.forEach((slide, i) => {
      slide.setAttribute('role', 'tabpanel');
      slide.setAttribute('id', `slide-${i}`);
    });

    // Create live region for announcements
    this.createLiveRegion();
  }

  /**
   * Create ARIA live region for announcements
   */
  createLiveRegion() {
    const liveRegion = document.createElement('div');
    liveRegion.className = 'visually-hidden';
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('aria-atomic', 'true');
    liveRegion.id = `gallery-announcer-${this.gallery.id}`;
    this.gallery.appendChild(liveRegion);
    this.liveRegion = liveRegion;
  }

  /**
   * Announce slide change to screen readers
   * @param {number} newIndex - New slide index
   */
  announceSlideChange(newIndex) {
    if (!this.liveRegion) return;

    const message = `Image ${newIndex + 1} of ${this.slides.length}`;
    this.liveRegion.textContent = message;
  }

  /**
   * Pause all playing media (videos, iframes, 3D models)
   */
  pauseAllMedia() {
    // Pause HTML5 videos
    this.gallery.querySelectorAll('video').forEach((video) => {
      video.pause();
    });

    // Pause YouTube videos
    this.gallery.querySelectorAll('iframe.js-youtube').forEach((iframe) => {
      iframe.contentWindow?.postMessage(
        JSON.stringify({ event: 'command', func: 'pauseVideo', args: '' }),
        '*',
      );
    });

    // Pause Vimeo videos
    this.gallery.querySelectorAll('iframe.js-vimeo').forEach((iframe) => {
      iframe.contentWindow?.postMessage(
        JSON.stringify({ method: 'pause' }),
        '*',
      );
    });

    // Pause 3D models
    this.gallery.querySelectorAll('model-viewer').forEach((model) => {
      model.pause?.();
    });
  }
}

// Initialize all product galleries
document.addEventListener('DOMContentLoaded', function () {
  const galleries = document.querySelectorAll('.product-custom-gallery');

  galleries.forEach((gallery) => {
    try {
      // Store reference on the element for external access (e.g., variant switching)
      gallery._galleryNavigation = new ProductGalleryNavigation(gallery);
    } catch (error) {
      console.error('Failed to initialize product gallery:', error);
    }
  });
});
